import React from 'react';
import { ArrowRight, Sparkles, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLang } from '../context/LanguageContext';
import { trackWhatsAppClick, trackConsultationCTAClick, trackEvent } from '../utils/analytics';
import GoldenCircleTrustBadge from './GoldenCircleTrustBadge';

const ASTROLOGER_PHOTO = '/media/founder/IMG_9620-removebg-preview.png';
const HERO_BG = '/media/stock/82f6f35e_photo-1687506765495-88a298ab900b.jpgs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxuaWdodCUyMHNreSUyMHN0YXJzfGVufDB8fHx8MTc3NTAyMTc1NXww&ixlib=rb-4.1.0&q=85&w=1920';

const WhatsAppSVG = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
);

export default function Hero() {
  const { t } = useLang();
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-20" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at top center, rgba(35,11,69,0.7) 0%, rgba(9,5,20,0.85) 60%, rgba(9,5,20,0.95) 100%)' }} />

      {/* Mandala Rings */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] opacity-[0.04] pointer-events-none">
        <div className="w-full h-full border border-gold/30 rounded-full mandala-spin" />
        <div className="absolute inset-12 border border-purple-light/20 rounded-full mandala-spin-reverse" />
        <div className="absolute inset-24 border border-gold/20 rounded-full mandala-spin" style={{ animationDuration: '150s' }} />
      </div>

      {/* Purple Glow */}
      <motion.div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-purple/20 rounded-full blur-[120px]"
        animate={{ scale: [1, 1.1, 1], opacity: [0.15, 0.25, 0.15] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="absolute bottom-20 right-1/4 w-[400px] h-[400px] bg-gold/10 rounded-full blur-[100px]"
        animate={{ scale: [1, 1.15, 1], opacity: [0.08, 0.15, 0.08] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />

      {/* Floating Om */}
      <div className="absolute right-16 top-1/3 hidden xl:block om-float-1">
        <span className="text-7xl text-gold/10 om-glow font-heading">ॐ</span>
      </div>
      <div className="absolute left-12 bottom-1/3 hidden lg:block om-float-2">
        <span className="text-5xl text-purple-light/10 om-glow font-heading">ॐ</span>
      </div>

      {/* Content */}
      <div className="container-custom relative z-10">
        {/* MOBILE LAYOUT */}
        <div className="lg:hidden flex flex-col items-center text-center">
          {/* Photo — premium framed card matching desktop treatment */}
          <motion.div className="relative w-full mb-4 -mt-4 flex justify-center"
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}>
            {/* Mandala rings — subtle, matches desktop */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92%] max-w-[360px] aspect-square pointer-events-none">
              <div className="absolute inset-0 rounded-full border border-gold/15 mandala-spin" style={{ animationDuration: '40s' }} />
              <div className="absolute inset-4 rounded-full border border-purple-light/10 mandala-spin-reverse" style={{ animationDuration: '60s' }} />
            </div>
            {/* Soft golden glow behind */}
            <motion.div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] max-w-[280px] aspect-square bg-gold/12 rounded-full blur-[70px] pointer-events-none"
              animate={{ opacity: [0.35, 0.6, 0.35] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
            {/* Framed portrait card — gold border + backdrop for transparent PNG */}
            <div className="relative rounded-[1.75rem] overflow-hidden border border-gold/25 shadow-[0_0_36px_rgba(255,215,0,0.14)] bg-gradient-to-b from-purple/15 via-transparent to-background/60"
                 style={{ width: '82%', maxWidth: '320px' }}>
              <img src={ASTROLOGER_PHOTO} alt="Vivek Mudgal - Astrologer"
                className="relative w-full h-auto object-cover"
                style={{ transform: 'scaleX(-1)' }}
                data-testid="hero-astrologer-photo" />
              {/* Bottom gradient fade to blend PNG bottom into card */}
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background/85 to-transparent pointer-events-none" />
            </div>
          </motion.div>

          {/* Name - large, bold */}
          <motion.div className="mb-3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <h1 className="font-heading text-5xl font-medium tracking-tight leading-none">
              <span className="text-text-main">Vivek </span>
              <span className="text-gold-gradient">Mudgal</span>
            </h1>
            <p className="text-gold/70 text-xs tracking-[0.25em] uppercase mt-3 font-semibold">Astrologer | Vastu Expert | Cosmic Healer</p>
          </motion.div>

          {/* Sanskrit Shloka */}
          <motion.p className="text-gold/50 text-sm font-heading italic mb-4 px-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
            "ज्योतिषं सूर्यादि ग्रहाणां बोधकं शास्त्रम्"
          </motion.p>

          {/* 33+ Years Badge */}
          <motion.div className="inline-flex items-center gap-2 mb-6 px-6 py-3 rounded-full border border-gold/30 bg-gold/5 backdrop-blur-md"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="text-sm text-gold font-medium">{t('hero.badge')}</span>
          </motion.div>

          {/* Description */}
          <motion.p className="text-base text-text-muted/80 max-w-sm mx-auto mb-8 leading-relaxed px-2"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
            {t('hero.desc')}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div className="flex flex-col sm:flex-row gap-3 w-full px-4"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}>
            <a href="#book-consultation"
              onClick={() => trackConsultationCTAClick('hero_mobile')}
              className="btn-gold flex items-center justify-center gap-2 text-base w-full" data-testid="hero-consultation-btn-mobile">
              {t('hero.bookConsultation')} <ArrowRight className="w-5 h-5" />
            </a>
            <a href="https://wa.me/919311369729?text=%E0%A4%A8%E0%A4%AE%E0%A4%B8%E0%A5%8D%E0%A4%A4%E0%A5%87%20%E0%A4%86%E0%A4%9A%E0%A4%BE%E0%A4%B0%E0%A5%8D%E0%A4%AF%20%E0%A4%9C%E0%A5%80%2C%20%E0%A4%AE%E0%A5%81%E0%A4%9D%E0%A5%87%20%E0%A4%AA%E0%A4%B0%E0%A4%BE%E0%A4%AE%E0%A4%B0%E0%A5%8D%E0%A4%B6%20%E0%A4%9A%E0%A4%BE%E0%A4%B9%E0%A4%BF%E0%A4%8F%E0%A5%A4" target="_blank" rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('hero_mobile')}
              className="flex items-center justify-center gap-2 px-6 py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-full font-semibold transition-all text-base w-full"
              data-testid="hero-whatsapp-btn-mobile">
              <WhatsAppSVG /> WhatsApp
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div className="flex gap-8 justify-center mt-10"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
            {[
              { value: '33+', label: t('hero.years') },
              { value: '33K+', label: t('hero.clients') },
              { value: '14+', label: t('hero.countries') },
            ].map((stat, i) => (
              <div key={i} className="text-center" data-testid={`hero-stat-${i}`}>
                <p className="font-heading text-3xl font-medium stat-number">{stat.value}</p>
                <p className="text-xs text-text-muted mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>

          {/* Golden Circle Trust Badge — Mobile */}
          <div className="w-full mt-8 px-2">
            <GoldenCircleTrustBadge align="center" />
          </div>
        </div>

        {/* DESKTOP LAYOUT */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Text */}
          <motion.div className="text-left"
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}>
            <motion.p className="text-gold/40 text-sm font-heading italic mb-6 tracking-wide"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
              "ज्योतिषं सूर्यादि ग्रहाणां बोधकं शास्त्रम्"
            </motion.p>
            <motion.div className="inline-flex items-center gap-2 mb-8 px-5 py-2 rounded-full border border-gold/20 bg-white/5 backdrop-blur-md"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="text-sm text-text-muted">{t('hero.badge')}</span>
            </motion.div>
            <motion.h1 className="font-heading text-6xl lg:text-7xl font-medium tracking-tight leading-none mb-6"
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
              {t('hero.heading1')}
              <span className="block text-gold-gradient mt-3">{t('hero.heading2')}</span>
            </motion.h1>
            <motion.p className="text-xl md:text-2xl text-gold/60 mb-4 font-heading italic"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
              {t('hero.subtitle')}
            </motion.p>
            <motion.p className="text-lg text-text-muted/80 max-w-xl mb-10 leading-relaxed"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
              {t('hero.desc')}
            </motion.p>
            <motion.div className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
              <a href="#book-consultation"
                onClick={() => trackConsultationCTAClick('hero_desktop')}
                className="btn-gold flex items-center gap-2 text-base" data-testid="hero-consultation-btn">
                {t('hero.bookConsultation')} <ArrowRight className="w-5 h-5" />
              </a>
              <a href="#kundli"
                onClick={() => trackEvent('free_kundli_cta_click', { cta_source: 'hero_desktop' })}
                className="btn-gold-outline text-base" data-testid="hero-kundli-btn">
                {t('hero.freeKundli')}
              </a>
              <a href="https://wa.me/919311369729?text=%E0%A4%A8%E0%A4%AE%E0%A4%B8%E0%A5%8D%E0%A4%A4%E0%A5%87%20%E0%A4%86%E0%A4%9A%E0%A4%BE%E0%A4%B0%E0%A5%8D%E0%A4%AF%20%E0%A4%9C%E0%A5%80%2C%20%E0%A4%AE%E0%A5%81%E0%A4%9D%E0%A5%87%20%E0%A4%AA%E0%A4%B0%E0%A4%BE%E0%A4%AE%E0%A4%B0%E0%A5%8D%E0%A4%B6%20%E0%A4%9A%E0%A4%BE%E0%A4%B9%E0%A4%BF%E0%A4%8F%E0%A5%A4" target="_blank" rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick('hero_desktop')}
                className="flex items-center gap-2 px-6 py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-full font-semibold transition-all text-base"
                data-testid="hero-whatsapp-btn">
                <WhatsAppSVG /> WhatsApp
              </a>
            </motion.div>
            <motion.div className="flex flex-wrap gap-10 mt-14"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
              {[
                { value: '33+', label: t('hero.years') },
                { value: '33K+', label: t('hero.clients') },
                { value: '14+', label: t('hero.countries') },
              ].map((stat, i) => (
                <div key={i} data-testid={`hero-stat-${i}`}>
                  <p className="font-heading text-4xl font-medium stat-number">{stat.value}</p>
                  <p className="text-sm text-text-muted mt-1">{stat.label}</p>
                </div>
              ))}
            </motion.div>

            {/* Golden Circle Trust Badge — Desktop */}
            <div className="mt-10">
              <GoldenCircleTrustBadge align="left" />
            </div>
          </motion.div>

          {/* Right - Photo */}
          <motion.div className="relative flex justify-center"
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5 }}>
            <div className="relative">
              <div className="absolute -inset-8 rounded-full border border-gold/10 mandala-spin" style={{ animationDuration: '40s' }} />
              <div className="absolute -inset-16 rounded-full border border-purple-light/5 mandala-spin-reverse" style={{ animationDuration: '60s' }} />
              <div className="absolute -inset-4 bg-gradient-to-br from-gold/15 via-purple/10 to-gold/5 rounded-[2rem] blur-2xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-gold/20 glow-gold" style={{ maxWidth: '400px' }}>
                <img src={ASTROLOGER_PHOTO} alt="Vivek Mudgal - Astrologer" className="w-full h-auto object-cover" style={{ transform: 'scaleX(-1)' }}
                  data-testid="hero-astrologer-photo" />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                  <p className="font-heading text-2xl text-gold-gradient font-medium">Vivek Mudgal</p>
                  <p className="text-text-muted text-xs mt-1 tracking-wider uppercase">Astrologer | Vastu Expert | Cosmic Healer</p>
                </div>
              </div>
              <motion.div className="absolute -left-10 top-1/4 bg-surface/90 backdrop-blur-xl border border-gold/20 rounded-2xl px-5 py-3 glow-gold"
                animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
                <p className="font-heading text-3xl text-gold-gradient font-medium">33+</p>
                <p className="text-xs text-text-muted">Years Exp.</p>
              </motion.div>
              <motion.div className="absolute -right-6 bottom-1/4 bg-surface/90 backdrop-blur-xl border border-purple-light/20 rounded-2xl px-4 py-3"
                animate={{ y: [0, 8, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-gold fill-gold" />)}
                </div>
                <p className="text-xs text-text-muted mt-1">33K+ Reviews</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom divine divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <div className="h-32 bg-gradient-to-t from-background to-transparent" />
        <div className="divine-divider" />
      </div>
    </section>
  );
}
